import { WorkawareApi } from 'apis/WorkawareApi'
import { TrainingSummaryDto } from 'data/dtos/TrainingSummaryDTO\'s/TrainingSummaryDto'
import { transform, TransformMethods } from 'data/transform'

// Short-lived cache so navigating between UserCertifications and UserPolicies
// (which both call GetSummary) does not fire duplicate requests.
// TTL: 2 minutes — the dashboard warms this cache so the certifications page opens instantly;
// mutations call invalidateSummaryCache().
const SUMMARY_CACHE_TTL_MS = 120_000
const summaryCache = new Map<string, { data: any; expiresAt: number }>()

export class trainingMatrixService {
    private _api: WorkawareApi
    private _trainingMatrixTransform: TransformMethods<TrainingSummaryDto>

    constructor(api: WorkawareApi) {
        this._api = api
        this._trainingMatrixTransform = transform<TrainingSummaryDto>(TrainingSummaryDto)
    }
    getSummarys = (userId?: number) => {
        const cacheKey = userId != null ? `summary_${userId}` : 'summary_all'
        const cached = summaryCache.get(cacheKey)
        if (cached && Date.now() < cached.expiresAt) {
            return Promise.resolve(cached.data)
        }
        return new Promise<any>((resolve, reject) => {
            this._api.trainingMatrix
                .getSummary(userId)
                .then((result) => {
                    summaryCache.set(cacheKey, { data: result, expiresAt: Date.now() + SUMMARY_CACHE_TTL_MS })
                    resolve(result)
                })
                .catch((error) => reject(error))
        })
    }
    // Call this after any mutation that affects the summary (e.g. assigning a course)
    invalidateSummaryCache = () => summaryCache.clear()
    getActivePolicies = () => {
        return new Promise<any>((resolve, reject) => {
            this._api.trainingMatrix.policies
                .getActive()
                .then((result) => resolve(result.value || result))
                .catch((error) => reject(error))
        })
    }
    runPolicyReminders = (policyId: number) => {
        return new Promise<any>((resolve, reject) => {
            this._api.trainingMatrix.policies
                .remindAll(policyId)
                .then((result) => resolve(result))
                .catch((error) => reject(error))
        })
    }
    runUserReminder = (policyId: number, userId: number) => {
        return new Promise<any>((resolve, reject) => {
            this._api.trainingMatrix.policies
                .remindUser(policyId, userId)
                .then((result) => resolve(result))
                .catch((error) => reject(error))
        })
    }
    assignPolicyToUser = (policyId: number, userId: number, companyId: number, currentUserId: number) => {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                UserId: userId,
                AcknowledgeableDocumentsId: policyId,
                CompanyId: companyId,
                CreatedByUserId: currentUserId,
                CreatedOn: new Date().toISOString(),
                IPAddress: 'webapp-assigned'
            }
            this._api.trainingMatrix.userAcknowledgedDocuments
                .assign(payload)
                .then((result) => resolve(result))
                .catch((error) => reject(error))
        })
    }
    assignPolicyToMultipleUsers = (policyId: number, userIds: number[], companyId: number, currentUserId: number) => {
        return new Promise<any>((resolve, reject) => {
            const promises = userIds.map(userId =>
                this.assignPolicyToUser(policyId, userId, companyId, currentUserId)
            )
            Promise.all(promises)
                .then((results) => resolve(results))
                .catch((error) => reject(error))
        })
    }
}